import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { CheckCircle, CircleNotch } from "@phosphor-icons/react";

interface LoginOtpStepProps {
  email: string;
  otp: string;
  onOtpChange: (value: string) => void;
  invalid: boolean;
  loading: boolean;
  resendCooldown: number;
  onSubmit: (e: React.FormEvent) => void;
  onResend: () => void;
  onChangeEmail: () => void;
}

export function LoginOtpStep({
  email,
  otp,
  onOtpChange,
  invalid,
  loading,
  resendCooldown,
  onSubmit,
  onResend,
  onChangeEmail,
}: LoginOtpStepProps) {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="animate-ink-rise text-center">
      <div
        className="mx-auto flex size-12 items-center justify-center rounded-full"
        style={{ background: "var(--color-brand-bg)" }}
      >
        <CheckCircle
          weight="fill"
          className="size-6"
          style={{ color: "var(--color-brand)" }}
        />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">
        {t("login.otpSent")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("login.checkInbox")}{" "}
        <span className="font-medium text-foreground">{email}</span>
      </p>
      <Label htmlFor="login-otp" className="mt-5 block text-left text-xs">
        {t("login.otpLabel")}
      </Label>
      <OtpCodeInput
        id="login-otp"
        value={otp}
        onChange={onOtpChange}
        invalid={invalid}
        autoFocus
        className="mt-2"
      />
      <Button
        type="submit"
        size="lg"
        disabled={loading || otp.length < 6}
        className="mt-4 h-12 w-full rounded-full text-sm font-semibold shadow-[0_10px_24px_-10px_var(--color-brand-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-10px_var(--color-brand-shadow-hover)]"
      >
        {loading ? (
          <CircleNotch className="animate-spin" data-icon="inline-start" />
        ) : null}
        {loading ? t("login.verifying") : t("login.verifyButton")}
      </Button>
      <div className="mt-4 flex items-center justify-center gap-4 text-xs">
        <button
          type="button"
          onClick={onResend}
          disabled={loading || resendCooldown > 0}
          className="text-muted-foreground transition-colors hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
        >
          {t("login.resend")}
          {resendCooldown > 0 ? ` (${resendCooldown}s)` : ""}
        </button>
        <span aria-hidden className="text-border">
          •
        </span>
        <button
          type="button"
          onClick={onChangeEmail}
          className="text-muted-foreground transition-colors hover:text-foreground hover:underline"
        >
          {t("login.otpUseDifferent")}
        </button>
      </div>
    </form>
  );
}
